import { Fragment, useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { RefreshCw, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import AnnouncementCallsPanel from "@/components/AnnouncementCallsPanel";
import CallTemplatesPanel from "@/components/CallTemplatesPanel";
import {
  CallQueueIndicator, VoiceCallsFilters, SummarySection, SessionBreakdown, PauseToggleCompact,
  BirthdayCallsPanel, AbsentCallsPanel, WebhookPanel, CallLogTable, PurgeDialog, ReachabilityPanel,
} from "@/pages/voicecalls/VoiceCallsParts";

export default function VoiceCalls({ embedded } = {}) {
  const { user } = useAuth();
  const todayLocalISO = () => {
    const d = new Date();
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
  };
  const [date, setDate] = useState(todayLocalISO());  // default to today's calls
  const [status, setStatus] = useState("all");
  const [classFilter, setClassFilter] = useState("all");   // admin-only
  const [sectionFilter, setSectionFilter] = useState("all"); // admin-only
  const [kindFilter, setKindFilter] = useState("all");     // all | absent | birthday
  const [activeTab, setActiveTab] = useState("absent");    // admin tabs: absent | birthday | reach
  const [deletingId, setDeletingId] = useState(null);
  const [clearingFailed, setClearingFailed] = useState(false);
  const [meta, setMeta] = useState({ classes: [], sections: [] });
  const [data, setData] = useState({ items: [], summary: { total: 0, success: 0, failed: 0, skipped: 0, pending: 0 } });
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [queue, setQueue] = useState(null);
  const [queuePeak, setQueuePeak] = useState(0);
  const [savingPause, setSavingPause] = useState(false);
  const [pauseReason, setPauseReason] = useState("");
  const [webhookInfo, setWebhookInfo] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgeDays, setPurgeDays] = useState("90");
  const [purging, setPurging] = useState(false);
  const [purgeTypedConfirm, setPurgeTypedConfirm] = useState("");
  const [purgePreview, setPurgePreview] = useState({ eligible: 0, days: 90 });

  // Birthday call panel
  const [birthdayPreview, setBirthdayPreview] = useState(null);
  const [triggeringBirthday, setTriggeringBirthday] = useState(false);
  const [schedHour, setSchedHour] = useState("22");
  const [schedMinute, setSchedMinute] = useState("22");
  const [savingSchedule, setSavingSchedule] = useState(false);

  // Absent calls panel (mirrors birthday card, read-only)
  const [absentPreview, setAbsentPreview] = useState(null);

  // Announcement-call modal
  const [announceOpen, setAnnounceOpen] = useState(false);

  // Call message templates modal
  const [templatesOpen, setTemplatesOpen] = useState(false);

  const isAdmin = user?.role === "admin";
  const isDevAdmin = !!user?.is_dev_admin;

  const loadAbsentPreview = async () => {
    if (!isAdmin) return;
    try {
      const { data } = await api.get("/admin/absent-calls/preview");
      setAbsentPreview(data);
    } catch (err) { console.warn("absent-calls/preview load failed:", err); }
  };

  const loadBirthdayPreview = async () => {
    if (!isAdmin) return;
    try {
      const { data } = await api.get("/admin/birthday-calls/preview");
      setBirthdayPreview(data);
      setSchedHour(String(data.scheduled_hour ?? 22));
      setSchedMinute(String(data.scheduled_minute ?? 0));
    } catch (err) { console.warn("birthday-calls/preview load failed:", err); }
  };

  const saveSchedule = async () => {
    const h = parseInt(schedHour, 10);
    const m = parseInt(schedMinute, 10);
    if (!Number.isFinite(h) || h < 0 || h > 23) return toast.error("Hour must be between 0 and 23.");
    if (!Number.isFinite(m) || m < 0 || m > 59) return toast.error("Minute must be between 0 and 59.");
    setSavingSchedule(true);
    try {
      const { data } = await api.put("/admin/birthday-calls/schedule", { hour: h, minute: m });
      toast.success(`Birthday cron set to ${String(data.scheduled_hour).padStart(2,"0")}:${String(data.scheduled_minute).padStart(2,"0")} ${data.tz}`);
      await loadBirthdayPreview();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not save schedule");
    } finally { setSavingSchedule(false); }
  };

  const triggerBirthdayNow = async () => {
    setTriggeringBirthday(true);
    try {
      const { data } = await api.post("/admin/birthday-calls/trigger");
      if (data.queued > 0) {
        toast.success(`Birthday calls queued for ${data.queued} student${data.queued === 1 ? "" : "s"}.`);
      } else if (data.matched > 0) {
        toast.info("All of today's birthdays have already been called.");
      } else {
        toast.message("No students have a birthday today.");
      }
      // Refresh both the preview list and the main calls table
      setTimeout(async () => {
        await loadBirthdayPreview();
        await load();
      }, 1200);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not trigger birthday calls");
    } finally { setTriggeringBirthday(false); }
  };

  const loadSettings = async () => {
    if (!isAdmin) return;
    try {
      const { data } = await api.get("/admin/settings");
      setSettings(data);
      setPauseReason(data.retell_pause_reason || "");
    } catch (err) { console.warn("admin/settings load failed:", err); }
    try {
      const { data } = await api.get("/admin/retell/webhook-info");
      setWebhookInfo(data);
    } catch (err) { console.warn("retell/webhook-info load failed:", err); }
    try {
      const { data } = await api.get("/admin/voice-calls/purge/preview");
      setPurgePreview(data);
    } catch (err) { console.warn("purge/preview load failed:", err); }
    await loadBirthdayPreview();
    await loadAbsentPreview();
  };
  useEffect(() => { loadSettings(); /* eslint-disable-next-line */ }, [isAdmin]);

  // Debounced re-fetch of the eligibility preview when the user types a different days value
  const refetchPurgePreview = useCallback(async (n) => {
    try {
      const { data } = await api.get("/admin/voice-calls/purge/preview", { params: { days: n } });
      setPurgePreview(data);
    } catch (err) { console.warn("purge/preview debounced refresh failed:", err); }
  }, []);
  useEffect(() => {
    if (!isAdmin || !purgeOpen) return undefined;
    const n = parseInt(purgeDays, 10);
    if (isNaN(n) || n <= 0) return undefined;
    const t = setTimeout(() => refetchPurgePreview(n), 250);
    return () => clearTimeout(t);
  }, [purgeDays, isAdmin, purgeOpen, refetchPurgePreview]);

  // Live call-queue indicator — polls the global outbound-call gate every 3s.
  useEffect(() => {
    let alive = true;
    const poll = async () => {
      try {
        const { data } = await api.get("/voice/queue-status");
        if (!alive) return;
        setQueue(data);
        const total = (data.active || 0) + (data.queued || 0);
        setQueuePeak((prev) => (total === 0 ? 0 : Math.max(prev, total)));
      } catch { /* silent */ }
    };
    poll();
    const id = setInterval(poll, 3000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const [reconciling, setReconciling] = useState(false);
  const reconcileCalls = async () => {
    setReconciling(true);
    try {
      const { data } = await api.post("/admin/voice-calls/reconcile", {}, { params: { days: 30 } });
      toast.success(`Reconciled with Retell — ${data.imported} imported, ${data.updated} updated (last ${data.days} days)`);
      await load();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Reconcile failed");
    } finally { setReconciling(false); }
  };

  const syncWebhook = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/retell/sync-webhook", {});
      toast.success("Webhook URL pushed to Retell ✓");
      setWebhookInfo((w) => ({ ...(w || {}), webhook_url: data.webhook_url, events: data.events }));
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Sync failed");
    } finally { setSyncing(false); }
  };

  const copyWebhook = async () => {
    if (!webhookInfo?.webhook_url) return;
    try {
      await navigator.clipboard.writeText(webhookInfo.webhook_url);
      toast.success("Webhook URL copied");
    } catch {
      toast.error("Could not copy");
    }
  };

  const runPurge = async () => {
    setPurging(true);
    try {
      const days = parseInt(purgeDays, 10);
      const body = isNaN(days) || days <= 0 ? {} : { days };
      const { data } = await api.post("/admin/voice-calls/purge", body);
      toast.success(`Purged ${data.deleted} call${data.deleted === 1 ? "" : "s"} older than ${data.days} day${data.days === 1 ? "" : "s"}`);
      setPurgeOpen(false);
      setPurgeTypedConfirm("");
      load();
      // Refresh preview so the button hides if nothing remains
      try {
        const { data: pv } = await api.get("/admin/voice-calls/purge/preview");
        setPurgePreview(pv);
      } catch (err) { console.warn("purge/preview post-purge refresh failed:", err); }
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Purge failed");
    } finally { setPurging(false); }
  };

  const [replayingId, setReplayingId] = useState(null);
  const replayCall = async (row) => {
    if (!row?.id) return;
    setReplayingId(row.id);
    try {
      const { data } = await api.post(`/admin/voice-calls/${row.id}/replay`);
      toast.success(`Retrying call for ${data.student_name || row.student_name}…`);
      // Keep the spinner visible until the background task has had time to write the new row
      setTimeout(async () => {
        try {
          await load();
          const { data: pv } = await api.get("/admin/voice-calls/purge/preview");
          setPurgePreview(pv);
        } catch (err) { console.warn("post-replay refresh failed:", err); }
        setReplayingId(null);
      }, 1500);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not retry call");
      setReplayingId(null);
    }
  };

  const togglePause = async (next) => {
    setSavingPause(true);
    try {
      const { data } = await api.put("/admin/settings", {
        retell_paused: next,
        retell_pause_reason: next ? (pauseReason || "Paused by admin") : "",
      });
      setSettings(data);
      toast.success(next ? "Retell calls paused" : "Retell calls resumed");
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not update setting");
    } finally { setSavingPause(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (date) params.date = date;
      if (status && status !== "all") params.status = status;
      if (classFilter && classFilter !== "all") params.class_name = classFilter;
      if (sectionFilter && sectionFilter !== "all") params.section = sectionFilter;
      if (kindFilter && kindFilter !== "all") {
        if (kindFilter === "wellbeing") {
          params.kind = "announcement";
          params.category = "wellbeing";
        } else {
          params.kind = kindFilter;
        }
      }
      const { data } = await api.get("/voice-calls", { params });
      setData(data);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Failed to load voice calls");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date, status, classFilter, sectionFilter, kindFilter]);

  // Admin tabs drive the call kind (Absent / Birthday); the "Who's not
  // answering" tab shows the reachability table and doesn't filter the log.
  useEffect(() => {
    if (!isAdmin) return;
    setKindFilter(activeTab === "birthday" ? "birthday" : activeTab === "absent" ? "absent" : "all");
  }, [activeTab, isAdmin]);

  const deleteCall = async (row) => {
    if (!row?.id) return;
    setDeletingId(row.id);
    try {
      const { data } = await api.delete(`/admin/voice-calls/${row.id}`);
      toast.success(`Deleted ${data.deleted} failed call log`);
      await load();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not delete call log");
    } finally { setDeletingId(null); }
  };

  const clearFailed = async () => {
    setClearingFailed(true);
    try {
      const params = {};
      if (date) params.date = date;
      if (classFilter && classFilter !== "all") params.class_name = classFilter;
      if (sectionFilter && sectionFilter !== "all") params.section = sectionFilter;
      if (kindFilter && kindFilter !== "all" && kindFilter !== "wellbeing") params.kind = kindFilter;
      const { data } = await api.post("/admin/voice-calls/delete-failed", {}, { params });
      toast.success(`Cleared ${data.deleted} failed/skipped call${data.deleted === 1 ? "" : "s"}`);
      await load();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not clear failed calls");
    } finally { setClearingFailed(false); }
  };

  // Admins need the full class/section list for the filter dropdowns
  useEffect(() => {
    if (!isAdmin) return;
    api.get("/meta/classes").then(({ data }) => setMeta({ classes: data.classes, sections: data.sections })).catch((err) => console.warn("meta/classes load failed:", err));
  }, [isAdmin]);

  const Wrap = embedded ? "div" : Layout;
  const failedCount = (data.summary?.failed || 0) + (data.summary?.skipped || 0);
  const clearFailedBtn = isAdmin && failedCount > 0 ? (
    <div className="flex justify-end mb-3">
      <Button
        variant="outline"
        onClick={clearFailed}
        disabled={clearingFailed}
        className="h-9 rounded-full border-rose-200 text-rose-600 hover:bg-rose-50"
        data-testid="clear-failed-btn"
      >
        <Trash2 className="h-4 w-4 mr-1" />
        {clearingFailed ? "Clearing…" : `Clear failed (${failedCount})`}
      </Button>
    </div>
  ) : null;
  const callLog = (
    <CallLogTable
      loading={loading}
      items={data.items}
      isAdmin={isAdmin}
      replayingId={replayingId}
      replayCall={replayCall}
      deletingId={deletingId}
      onDelete={deleteCall}
    />
  );
  // Dropdown filters + count cards — moved inside the Absent/Birthday tabs.
  const filtersBlock = (
    <>
      <div className="mb-4">
        <VoiceCallsFilters
          isAdmin={isAdmin}
          meta={meta}
          classFilter={classFilter}
          setClassFilter={setClassFilter}
          sectionFilter={sectionFilter}
          setSectionFilter={setSectionFilter}
          date={date}
          setDate={setDate}
          kindFilter={kindFilter}
          setKindFilter={setKindFilter}
          status={status}
          setStatus={setStatus}
          showKind={false}
          showFilters
          showActions={false}
          purgePreview={purgePreview}
        />
      </div>
      <SummarySection summary={data.summary} />
    </>
  );
  return (
    <Wrap>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-2">Parent notifications</div>
          <h1 className="text-3xl font-bold text-slate-900" style={{fontFamily:"Outfit"}}>Voice call log</h1>
          <p className="text-slate-500 mt-1">
            Automated calls placed to absent students&apos; parents via Retell AI.
            {user?.role === "admin"
              ? " As admin you can browse every class — use the filters to narrow down."
              : " You see calls only for the class you're the class teacher of."}
          </p>
          <CallQueueIndicator queue={isDevAdmin ? queue : null} peak={queuePeak} />
          {isDevAdmin && (
            <button
              type="button"
              onClick={reconcileCalls}
              disabled={reconciling}
              data-testid="reconcile-retell-btn"
              title="Pull recent calls from Retell and backfill any missing from this log"
              className="mt-3 inline-flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-4 h-9 text-sm font-medium text-sky-700 hover:bg-sky-100 disabled:opacity-60 transition-colors"
            >
              <RefreshCw className={`h-4 w-4 ${reconciling ? "animate-spin" : ""}`} />
              {reconciling ? "Reconciling…" : "Reconcile from Retell"}
            </button>
          )}
        </div>
        <div className="flex items-center gap-3 flex-wrap justify-end">
          {isAdmin && (
            <VoiceCallsFilters
              isAdmin={isAdmin}
              meta={meta}
              classFilter={classFilter}
              setClassFilter={setClassFilter}
              sectionFilter={sectionFilter}
              setSectionFilter={setSectionFilter}
              date={date}
              setDate={setDate}
              kindFilter={kindFilter}
              setKindFilter={setKindFilter}
              status={status}
              setStatus={setStatus}
              showFilters={false}
              showActions
              purgePreview={purgePreview}
              onOpenTemplates={() => setTemplatesOpen(true)}
              onOpenAnnounce={() => setAnnounceOpen(true)}
              onOpenPurge={() => { setPurgeTypedConfirm(""); setPurgeOpen(true); }}
            />
          )}
          {isAdmin && settings && (
            <PauseToggleCompact settings={settings} savingPause={savingPause} togglePause={togglePause} />
          )}
          {!isAdmin && (
            <VoiceCallsFilters
              isAdmin={isAdmin}
              meta={meta}
              date={date}
              setDate={setDate}
              kindFilter={kindFilter}
              setKindFilter={setKindFilter}
              status={status}
              setStatus={setStatus}
              showKind
              showFilters
              showActions={false}
              purgePreview={purgePreview}
            />
          )}
        </div>
      </div>

      {isDevAdmin && webhookInfo && (
        <WebhookPanel
          webhookInfo={webhookInfo}
          syncing={syncing}
          syncWebhook={syncWebhook}
          copyWebhook={copyWebhook}
        />
      )}

      {isAdmin ? (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-4" data-testid="calls-tabs">
            <TabsTrigger value="absent" data-testid="calls-tab-absent">Absent</TabsTrigger>
            <TabsTrigger value="birthday" data-testid="calls-tab-birthday">Birthday</TabsTrigger>
            <TabsTrigger value="reach" data-testid="calls-tab-reach">Who&apos;s not answering</TabsTrigger>
          </TabsList>

          <TabsContent value="absent" data-testid="calls-tabpanel-absent">
            {filtersBlock}
            <SessionBreakdown bySession={data.by_session} />
            <AbsentCallsPanel absentPreview={absentPreview} />
            {clearFailedBtn}
            {callLog}
          </TabsContent>

          <TabsContent value="birthday" data-testid="calls-tabpanel-birthday">
            {filtersBlock}
            <BirthdayCallsPanel
              birthdayPreview={birthdayPreview}
              triggeringBirthday={triggeringBirthday}
              triggerBirthdayNow={triggerBirthdayNow}
              schedHour={schedHour}
              setSchedHour={setSchedHour}
              schedMinute={schedMinute}
              setSchedMinute={setSchedMinute}
              savingSchedule={savingSchedule}
              saveSchedule={saveSchedule}
            />
            {clearFailedBtn}
            {callLog}
          </TabsContent>

          <TabsContent value="reach" data-testid="calls-tabpanel-reach">
            <ReachabilityPanel />
          </TabsContent>
        </Tabs>
      ) : (
        <>
          <SummarySection summary={data.summary} />
          <SessionBreakdown bySession={data.by_session} />
          {callLog}
        </>
      )}

      <PurgeDialog
        open={purgeOpen}
        onOpenChange={(v) => { setPurgeOpen(v); if (!v) setPurgeTypedConfirm(""); }}
        purgeDays={purgeDays}
        setPurgeDays={setPurgeDays}
        purgePreview={purgePreview}
        purgeTypedConfirm={purgeTypedConfirm}
        setPurgeTypedConfirm={setPurgeTypedConfirm}
        purging={purging}
        runPurge={runPurge}
      />

      {isAdmin && (
        <AnnouncementCallsPanel
          isOpen={announceOpen}
          onClose={() => setAnnounceOpen(false)}
          onSent={() => { load(); }}
        />
      )}
      {isAdmin && (
        <CallTemplatesPanel
          isOpen={templatesOpen}
          onClose={() => setTemplatesOpen(false)}
        />
      )}
    </Wrap>
  );
}
